import { doc, getDoc, runTransaction } from "firebase/firestore";
import { db } from "../firebase";
import { MAX_SCHOOLS_PER_USER, normalizeEmail } from "./outreachMvpCredentials";
import { QUIZ_CONFIRM_TYPES } from "./quizSeason";
import { archiveStaleQuizForSchool } from "./quizArchiveFirestore";

const DOC_PATH = ["analytics_config", "outreach_tracking"];

export const CALL_STATUS_OPTIONS = [
  { id: "not_called", label: "Not called" },
  { id: "answered", label: "Answered" },
  { id: "no_answer", label: "No answer" },
  { id: "busy", label: "Busy" },
  { id: "callback", label: "Callback later" },
];

const DEFAULT_STATE = {
  schoolState: {},
};

function trackingDocRef() {
  return doc(db, ...DOC_PATH);
}

export async function loadOutreachTracking() {
  try {
    const snap = await getDoc(trackingDocRef());
    if (!snap.exists()) return { ...DEFAULT_STATE };
    const data = snap.data();
    return {
      schoolState:
        data.schoolState && typeof data.schoolState === "object"
          ? data.schoolState
          : {},
    };
  } catch (err) {
    console.error("Failed to load outreach tracking:", err);
    return { ...DEFAULT_STATE };
  }
}

function parseQuizReturningTeams(raw) {
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(2, Math.floor(n));
}

const VALID_CONFIRM_TYPES = new Set(Object.values(QUIZ_CONFIRM_TYPES));

function parseQuizConfirmType(raw) {
  if (!raw || !VALID_CONFIRM_TYPES.has(raw)) return null;
  return raw;
}

export function getSchoolTracking(schoolState, schoolKey) {
  const s = schoolState[schoolKey] || {};
  const lockedByEmail = normalizeEmail(s.lockedByEmail);
  return {
    lockedByEmail,
    lockedAt: s.lockedAt ?? "",
    callStatus: lockedByEmail ? (s.callStatus ?? "not_called") : "not_called",
    messageSent: lockedByEmail ? Boolean(s.messageSent) : false,
    notes: s.notes ?? "",
    contactPhone: s.contactPhone ?? "",
    contactRole: s.contactRole ?? "",
    quizReturningTeams: parseQuizReturningTeams(s.quizReturningTeams),
    quizConfirmType: parseQuizConfirmType(s.quizConfirmType),
    quizArchivedCount: Number(s.quizArchivedCount) || 0,
    quizConfirmedAt: s.quizConfirmedAt ?? "",
    quizConfirmedBy: s.quizConfirmedBy ?? "",
    updatedAt: s.updatedAt ?? "",
    updatedBy: s.updatedBy ?? "",
  };
}

export function countLockedSchoolsForEmail(schoolState, userEmail) {
  const email = normalizeEmail(userEmail);
  let count = 0;
  for (const key of Object.keys(schoolState)) {
    if (normalizeEmail(schoolState[key]?.lockedByEmail) === email) {
      count++;
    }
  }
  return count;
}

export class OutreachClaimError extends Error {
  constructor(message) {
    super(message);
    this.name = "OutreachClaimError";
  }
}

/**
 * Atomically claim a school for a user. Fails if already locked or user has 7 schools.
 */
export async function claimSchoolForEmail({ schoolKey, userEmail }) {
  const email = normalizeEmail(userEmail);
  if (!email) throw new OutreachClaimError("Not signed in.");

  const result = await runTransaction(db, async (transaction) => {
    const ref = trackingDocRef();
    const snap = await transaction.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const schoolState = { ...(data.schoolState || {}) };
    const current = getSchoolTracking(schoolState, schoolKey);

    if (current.lockedByEmail) {
      if (current.lockedByEmail === email) {
        return { schoolState, alreadyOwned: true };
      }
      throw new OutreachClaimError("This school is already claimed by someone else.");
    }

    const ownedCount = countLockedSchoolsForEmail(schoolState, email);
    if (ownedCount >= MAX_SCHOOLS_PER_USER) {
      throw new OutreachClaimError(
        `You can only claim up to ${MAX_SCHOOLS_PER_USER} schools.`
      );
    }

    const now = new Date().toISOString();
    schoolState[schoolKey] = {
      lockedByEmail: email,
      lockedAt: now,
      callStatus: "not_called",
      messageSent: false,
      notes: "",
      updatedAt: now,
      updatedBy: email,
    };

    transaction.set(
      ref,
      { schoolState, updatedAt: now },
      { merge: true }
    );

    return { schoolState, alreadyOwned: false };
  });

  return result;
}

/**
 * Update call/message/notes for a school — only owner or admin.
 */
export async function updateSchoolTrackingForEmail({
  schoolKey,
  userEmail,
  isAdmin,
  patch,
}) {
  const email = normalizeEmail(userEmail);
  const allowedKeys = ["callStatus", "messageSent", "notes", "quizReturningTeams", "contactPhone", "contactRole"];
  const safePatch = {};
  for (const key of allowedKeys) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      safePatch[key] = patch[key];
    }
  }
  if (Object.keys(safePatch).length === 0) return null;

  if (Object.prototype.hasOwnProperty.call(safePatch, "quizReturningTeams")) {
    const parsed = parseQuizReturningTeams(safePatch.quizReturningTeams);
    if (parsed === null) {
      delete safePatch.quizReturningTeams;
    } else {
      safePatch.quizReturningTeams = parsed;
      const now = new Date().toISOString();
      safePatch.quizConfirmedAt = now;
      safePatch.quizConfirmedBy = email || "admin";
    }
  }

  const result = await runTransaction(db, async (transaction) => {
    const ref = trackingDocRef();
    const snap = await transaction.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const schoolState = { ...(data.schoolState || {}) };
    const current = getSchoolTracking(schoolState, schoolKey);

    if (!current.lockedByEmail) {
      throw new OutreachClaimError("School is not claimed yet.");
    }

    if (!isAdmin && current.lockedByEmail !== email) {
      throw new OutreachClaimError("You can only update schools assigned to you.");
    }

    const now = new Date().toISOString();
    schoolState[schoolKey] = {
      ...schoolState[schoolKey],
      ...safePatch,
      updatedAt: now,
      updatedBy: email || "admin",
    };

    transaction.set(
      ref,
      { schoolState, updatedAt: now },
      { merge: true }
    );

    return { schoolState };
  });

  return result;
}

/**
 * Confirm last-year quiz status, archive stale docs, update tracking.
 */
export async function updateQuizConfirmationForSchool({
  schoolKey,
  userEmail,
  confirmType,
  returningTeams = 0,
  aliasGroups = [],
}) {
  const email = normalizeEmail(userEmail) || "admin";
  const type = parseQuizConfirmType(confirmType);
  if (!type) {
    throw new OutreachClaimError("Choose how last year's quiz teams are handled.");
  }

  let teams = 0;
  if (type === QUIZ_CONFIRM_TYPES.SAME_RETURNING) {
    const parsed = parseQuizReturningTeams(returningTeams);
    if (parsed === null || parsed < 1) {
      throw new OutreachClaimError(
        "Select how many of the same team(s) are returning."
      );
    }
    teams = parsed;
  }

  const archivedCount = await archiveStaleQuizForSchool(schoolKey, aliasGroups);

  const result = await runTransaction(db, async (transaction) => {
    const ref = trackingDocRef();
    const snap = await transaction.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const schoolState = { ...(data.schoolState || {}) };
    const now = new Date().toISOString();

    schoolState[schoolKey] = {
      ...(schoolState[schoolKey] || {}),
      quizConfirmType: type,
      quizReturningTeams: teams,
      quizConfirmedAt: now,
      quizConfirmedBy: email,
      quizArchivedCount: archivedCount,
      updatedAt: now,
      updatedBy: email,
    };

    transaction.set(ref, { schoolState, updatedAt: now }, { merge: true });
    return { schoolState, archivedCount };
  });

  return result;
}
